const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface Project {
  id: number;
  project_id: string; // ID alphanumérique de 15 caractères
  purchase_request_id: number;
  client_id: number;
  client_name: string;
  client_email: string;
  client_phone?: string;
  client_company?: string;
  status: 'created' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  created_at: string;
  updated_at: string;
  created_by: number; // ID de l'admin qui a créé le projet
  created_by_name: string;
  
  // Informations de la facture
  invoice_id: string;
  total_amount: number;
  discount_percentage?: number;
  
  // Services demandés
  services: ProjectService[];
  
  // Métadonnées
  description?: string;
  notes?: string;
  estimated_duration?: number; // en jours
  start_date?: string;
  end_date?: string;
}

export interface ProjectService {
  id: number;
  service_name: string;
  pack_name?: string;
  type: 'service' | 'pack';
  quantity: number;
  unit_price: number;
  total_price: number;
  description?: string;
  pages?: number;
}

class ProjectApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    console.log('🔑 Token utilisé pour projets:', token ? `${token.substring(0, 20)}...` : 'AUCUN TOKEN');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  // Créer un projet à partir d'une facture confirmée (Admin uniquement)
  async createProjectFromInvoice(purchaseRequestId: number): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/create-from-invoice/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ purchase_request_id: purchaseRequestId }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      throw error;
    }
  }

  // Récupérer tous les projets (Admin)
  async getAllProjects(): Promise<Project[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des projets:', error);
      throw error;
    }
  }

  // Récupérer les projets d'un client (Client)
  async getUserProjects(): Promise<Project[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/user/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des projets utilisateur:', error);
      throw error;
    }
  }

  // Récupérer un projet par ID
  async getProject(projectId: string): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error);
      throw error;
    }
  }

  // Mettre à jour le statut d'un projet (Admin)
  async updateProjectStatus(projectId: string, status: Project['status']): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/status/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }

  // Mettre à jour un projet (Admin)
  async updateProject(projectId: string, data: Partial<Project>): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du projet:', error);
      throw error;
    }
  }
}

export const projectApiService = new ProjectApiService();
